import {
  EMPTY_SELECTION,
  EMPTY_SUMMARY,
  type DrilledEdge,
  type Point2,
  type Selection,
  type SelectionRef,
  type Store,
  type ViewportState,
  addToSelection,
  createLens,
  drillSelection,
  removeFromSelection,
  selectionOf,
  summarizeSelection,
  toggleInSelection,
} from '@frontend/viewer-core';

import { PICK_TOLERANCE_PX } from '../constants';
import type { DrawingModel, DxfSessionState, SelectionManager } from './types';

export const createSelectionSlice = (
  state: Store<DxfSessionState>,
): Store<Selection> =>
  createLens(
    state,
    (current) => current.selection,
    (current, selection) => ({ ...current, selection }),
  );

export const createHoveredSlice = (
  state: Store<DxfSessionState>,
): Store<SelectionRef | null> =>
  createLens(
    state,
    (current) => current.hovered,
    (current, hovered) => ({ ...current, hovered }),
  );

type Dependencies = {
  /** Shared with `layerController` — hiding a layer prunes through here too. */
  selection: Store<Selection>;
  hovered: Store<SelectionRef | null>;
  getHiddenLayers: () => ReadonlySet<number>;
  getModel: () => DrawingModel | null;
  viewport: {
    getState(): ViewportState;
    screenToWorld(screen: Point2): Point2;
  };
};

/**
 * What is picked, hovered, and drilled into.
 *
 * Owns `drilledEdge` itself rather than exposing it: it is not one of the
 * session's stores because nothing outside this file needs to react to it,
 * only to the rule it enforces — every entry point that can leave it stale
 * (switching selection, clearing, loading a new drawing) goes through this
 * controller's own methods instead of a shared mutable cell.
 */
export const createSelectionController = ({
  selection,
  hovered,
  getHiddenLayers,
  getModel,
  viewport,
}: Dependencies): SelectionManager => {
  let drilledEdge: DrilledEdge = null;

  /** The nearest geometry to a screen point, with the chunk it belongs to. */
  const hitAt = (screenPoint: Point2) => {
    const model = getModel();
    if (!model) {
      return null;
    }
    const { scale } = viewport.getState();
    return model.index.pickAt(viewport.screenToWorld(screenPoint), {
      tolerance: PICK_TOLERANCE_PX / (scale || 1),
      hiddenLayers: getHiddenLayers(),
    });
  };

  return {
    getState: selection.getState,
    subscribe: selection.subscribe,
    set(refs) {
      // Selecting elsewhere ends the drill: a later double-click there should
      // open that edge, not jump back out to a loop the user has left behind.
      if (!refs.some((ref) => ref.loopId === drilledEdge?.loopId)) {
        drilledEdge = null;
      }
      selection.setState(() => selectionOf(refs));
    },
    add: (refs) =>
      selection.setState((previous) => addToSelection(previous, refs)),
    remove: (refs) =>
      selection.setState((previous) => removeFromSelection(previous, refs)),
    toggle: (refs) =>
      selection.setState((previous) => toggleInSelection(previous, refs)),
    clear() {
      drilledEdge = null;
      selection.setState(() => EMPTY_SELECTION);
      hovered.setState((previous) => (previous === null ? previous : null));
    },

    setHovered(ref) {
      hovered.setState((previous) => {
        const isSame =
          previous === ref ||
          (previous?.kind === 'loop' &&
            ref?.kind === 'loop' &&
            previous.loopId === ref.loopId) ||
          (previous?.kind === 'edge' &&
            ref?.kind === 'edge' &&
            previous.loopId === ref.loopId &&
            previous.chunkIndex === ref.chunkIndex);
        return isSame ? previous : ref;
      });
    },

    pickAt(screenPoint) {
      // Whole loops: a single click never picks an edge. Double-clicking is what
      // opens one, so a plain click on a busy drawing stays predictable.
      const hit = hitAt(screenPoint);
      return hit ? { kind: 'loop', loopId: hit.loopId } : null;
    },

    drillAt(screenPoint) {
      const hit = hitAt(screenPoint);
      if (!hit) {
        return;
      }
      const { ref, drilled } = drillSelection(hit, drilledEdge);
      drilledEdge = drilled;
      selection.setState(() => selectionOf([ref]));
    },

    summarize() {
      const model = getModel();
      return model
        ? summarizeSelection(selection.getState(), model.loops)
        : EMPTY_SUMMARY;
    },
  };
};
